import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { auth, db, functions } from '../firebaseConfig';
import '../styles/Dashboard.css';

import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Overview } from './dashboard/Overview';
import { Billing } from './dashboard/Billing';
import { Summaries } from './dashboard/Summaries';
import { useToast, ToastProvider } from '../components/ui/ToastContext';

const FUNCTIONS_BASE = 'https://us-central1-up-convert.cloudfunctions.net';
const SYNC_STRIPE_SUBSCRIPTION_URL = `${FUNCTIONS_BASE}/syncStripeSubscription`;

const PLANS = {
  price_1T7LSj4kuJmP9B1x17F7xZoC: { seats: 5, label: '5 Seats' },
  price_1T7LWL4kuJmP9B1x5Zk62N00: { seats: 10, label: '10 Seats' },
};

const STATUS_LABELS = {
  active: { text: 'Active', color: '#16a34a' },
  trialing: { text: 'Trialing', color: '#2563eb' },
  past_due: { text: 'Past Due', color: '#d97706' },
  canceled: { text: 'Canceled', color: '#dc2626' },
  unpaid: { text: 'Unpaid', color: '#dc2626' },
};

const SECTIONS = {
  overview: {
    title: 'Overview',
    description: 'Manage your workspace metrics, team members, and pending invites.',
  },
  summaries: {
    title: 'Call Summaries',
    description: 'Search and read transcripts and smart notes from all your team\'s calls.',
  },
  billing: {
    title: 'Plan & Billing',
    description: 'Manage your subscription, billing details, and seat capacity upgrades.',
  },
};

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0;
  return numeric > 10_000_000_000 ? numeric : numeric * 1000;
};

// We wrap the inner dashboard logic in a component to access the ToastContext
function DashboardContent({ section = 'overview' }) {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const billingSyncAttemptedRef = useRef(false);

  const activeSection = SECTIONS[section] ? section : 'overview';
  const sectionInfo = SECTIONS[activeSection];

  const [currentUser, setCurrentUser] = useState(null);
  const [orgId, setOrgId] = useState(null);

  const [stripeData, setStripeData] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [invites, setInvites] = useState([]);
  const [deleteInviteLoadingCode, setDeleteInviteLoadingCode] = useState('');

  const [members, setMembers] = useState([]);
  const [removeMemberLoadingUid, setRemoveMemberLoadingUid] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);

  const [workspaceMetrics, setWorkspaceMetrics] = useState({
    callsLast30Days: 0,
    userCallCounts: {},
    recentMeetings: []
  });

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }
      setCurrentUser(user);
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists()) {
          setOrgId(null);
          return;
        }
        const resolvedOrgId = userDoc.data()?.orgId || null;

        // Non-admin members should never land on the admin dashboard
        if (resolvedOrgId) {
          const memberDoc = await getDoc(doc(db, 'orgs', resolvedOrgId, 'members', user.uid));
          if (memberDoc.exists() && memberDoc.data()?.role !== 'admin') {
            navigate('/rep/dashboard', { replace: true });
            return;
          }
        }

        setOrgId(resolvedOrgId);
      } catch (_err) {
        setOrgId(null);
      }
    });

    return () => unsubscribeAuth();
  }, [navigate]);

  useEffect(() => {
    if (!orgId) return;

    const orgUnsub = onSnapshot(doc(db, 'orgs', orgId), (snap) => {
      if (snap.exists()) {
        setStripeData(snap.data()?.stripe || null);
      } else {
        setStripeData(null);
      }
    });

    const invitesQ = query(collection(db, 'orgs', orgId, 'invites'), orderBy('createdAt', 'desc'));
    const inviteUnsub = onSnapshot(invitesQ, (snap) => {
      const now = Date.now();
      const rows = snap.docs
        .map((d) => ({ code: String(d.id).toLowerCase(), ...d.data() }))
        .filter((inv) => {
          if (inv.status && inv.status !== 'pending') return false;
          if (!inv.expiresAt) return true;
          return toMillis(inv.expiresAt) > now;
        });
      setInvites(rows);
    });

    const membersQ = query(
      collection(db, 'orgs', orgId, 'members'),
      where('status', '==', 'active')
    );
    const membersUnsub = onSnapshot(membersQ, (snap) => {
      const rows = snap.docs
        .map((d) => ({ uid: d.id, ...d.data() }))
        .sort((a, b) => toMillis(b.joinedAt) - toMillis(a.joinedAt));
      setMembers(rows);
    });

    const fetchMetrics = async () => {
      try {
        if (!auth.currentUser) return;
        const idToken = await auth.currentUser.getIdToken();
        const res = await fetch(`${FUNCTIONS_BASE}/getWorkspaceMetrics`, {
          headers: { Authorization: `Bearer ${idToken}` }
        });
        if (res.ok) {
          const data = await res.json();
          setWorkspaceMetrics(data);
        }
      } catch (_) {
        // Keep the dashboard usable if metrics fail to load.
      }
    };
    fetchMetrics();

    return () => {
      orgUnsub();
      inviteUnsub();
      membersUnsub();
    };
  }, [orgId]);

  useEffect(() => {
    billingSyncAttemptedRef.current = false;
  }, [orgId]);

  useEffect(() => {
    if (!orgId || !auth.currentUser || billingSyncAttemptedRef.current) return;

    const queryParams = new URLSearchParams(window.location.search);
    const checkoutSuccess = queryParams.get('checkout') === 'success';
    const missingSubscriptionStatus = !!stripeData && !stripeData.status;
    if (!checkoutSuccess && !missingSubscriptionStatus) return;

    billingSyncAttemptedRef.current = true;

    const syncBillingStatus = async () => {
      try {
        const idToken = await auth.currentUser.getIdToken(true);
        const res = await fetch(SYNC_STRIPE_SUBSCRIPTION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({}),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data?.stripe) {
          setStripeData((prev) => ({ ...(prev || {}), ...data.stripe }));
          addToast('Subscription synchronized successfully.', 'success');
        }
      } catch (_err) {
        addToast('Failed to sync billing data.', 'error');
      } finally {
        if (checkoutSuccess) {
          queryParams.delete('checkout');
          const queryString = queryParams.toString();
          const nextUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}${window.location.hash || ''}`;
          window.history.replaceState({}, '', nextUrl);
        }
      }
    };

    syncBillingStatus();
  }, [orgId, stripeData, addToast]);

  const statusInfo = stripeData?.status ? STATUS_LABELS[stripeData.status] : null;
  const planLabel = stripeData?.priceId ? PLANS[stripeData.priceId]?.label : null;
  const hasActiveSub = !!stripeData?.status && ['active', 'trialing'].includes(stripeData.status);

  const seatsAllowed = Number(stripeData?.seatsAllowed || 0);
  const activeSeatCount = members.length;
  const pendingInviteCount = invites.length;
  const hasSeatPlan = seatsAllowed > 0;
  const seatsRemaining = hasSeatPlan ? Math.max(seatsAllowed - activeSeatCount, 0) : 0;

  const sortedPlans = useMemo(
    () => Object.entries(PLANS).sort(([, a], [, b]) => a.seats - b.seats),
    []
  );

  const handleCreateInvite = async () => {
    if (!hasActiveSub) {
      addToast('Subscription is not active. Subscribe or renew to invite members.', 'error');
      return;
    }

    if (!inviteEmail.trim()) {
      addToast('Please enter an email address.', 'error');
      return;
    }

    setInviteLoading(true);
    try {
      const createInviteFn = httpsCallable(functions, 'createInvite');
      const result = await createInviteFn({ role: 'user', email: inviteEmail.trim() });
      const inviteUrl = result.data?.inviteUrl || '';
      setInviteEmail('');

      // Copy the invite URL to clipboard automatically
      if (inviteUrl) {
        try {
          await navigator.clipboard.writeText(inviteUrl);
          addToast('Invite link copied to clipboard!', 'success');
        } catch (_) {
          addToast(`Invite created! Link: ${inviteUrl}`, 'success');
        }
      } else {
        addToast('Invite generated successfully!', 'success');
      }
    } catch (err) {
      addToast(err.message || 'Unable to generate invite code.', 'error');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleDeleteInvite = async (code) => {
    if (!code) return;
    const confirmed = window.confirm('Delete this invite code? It will no longer be usable.');
    if (!confirmed) return;

    setDeleteInviteLoadingCode(code);
    try {
      const deleteInviteFn = httpsCallable(functions, 'deleteInvite');
      await deleteInviteFn({ code });
      if (copiedCode === code) setCopiedCode(null);
      addToast(`Invite code ${code} deleted.`, 'success');
    } catch (err) {
      addToast(err.message || 'Unable to delete invite.', 'error');
    } finally {
      setDeleteInviteLoadingCode('');
    }
  };

  const handleRemoveMember = async (member) => {
    if (!member?.uid || member.uid === currentUser?.uid) return;

    const label = member.email || `user ${member.uid}`;
    const confirmed = window.confirm(`Remove ${label} and free up this seat?`);
    if (!confirmed) return;

    setRemoveMemberLoadingUid(member.uid);
    try {
      const removeMemberFn = httpsCallable(functions, 'removeMember');
      await removeMemberFn({ uid: member.uid });
      addToast(`Seat removed successfully.`, 'success');
    } catch (err) {
      addToast(err.message || 'Unable to remove seat.', 'error');
    } finally {
      setRemoveMemberLoadingUid('');
    }
  };

  const copyToClipboard = async (code) => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      addToast('Invite code copied to clipboard.', 'success');
      setTimeout(() => setCopiedCode(null), 1800);
    } catch (_err) {
      addToast('Clipboard permission denied. Copy manually.', 'error');
    }
  };

  const handleSubscribe = async (priceId) => {
    setCheckoutLoading(priceId);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(`${FUNCTIONS_BASE}/createCheckoutSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ priceId }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error(data.error || 'Could not open checkout.');
    } catch (err) {
      addToast(err.message || 'Billing action failed.', 'error');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManageBilling = async () => {
    setBillingLoading(true);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(`${FUNCTIONS_BASE}/createPortalSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ returnUrl: window.location.href }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error(data.error || 'Could not open billing portal.');
    } catch (err) {
      addToast(err.message || 'Unable to open billing portal.', 'error');
    } finally {
      setBillingLoading(false);
    }
  };

  // The DashboardLayout contains Sidebar, Topbar logic
  return (
    <DashboardLayout
      currentPlan={planLabel}
      activeSeatCount={activeSeatCount}
      seatsAllowed={seatsAllowed}
      statusInfo={statusInfo}
      title={sectionInfo.title}
      description={sectionInfo.description}
    >
      {activeSection === 'overview' && (
        <Overview
          currentUser={currentUser}
          statusInfo={statusInfo}
          planLabel={planLabel}
          hasSeatPlan={hasSeatPlan}
          activeSeatCount={activeSeatCount}
          seatsAllowed={seatsAllowed}
          seatsRemaining={seatsRemaining}
          pendingInviteCount={pendingInviteCount}
          members={members}
          invites={invites}
          hasActiveSub={hasActiveSub}
          stripeData={stripeData}
          inviteEmail={inviteEmail}
          setInviteEmail={setInviteEmail}
          handleCreateInvite={handleCreateInvite}
          inviteLoading={inviteLoading}
          deleteInviteLoadingCode={deleteInviteLoadingCode}
          handleDeleteInvite={handleDeleteInvite}
          handleRemoveMember={handleRemoveMember}
          removeMemberLoadingUid={removeMemberLoadingUid}
          copyToClipboard={copyToClipboard}
          copiedCode={copiedCode}
          workspaceMetrics={workspaceMetrics}
        />
      )}

      {activeSection === 'summaries' && (
        <Summaries currentUser={currentUser} />
      )}

      {activeSection === 'billing' && (
        <Billing
          billingLoading={billingLoading}
          handleManageBilling={handleManageBilling}
          statusInfo={statusInfo}
          planLabel={planLabel}
          stripeData={stripeData}
          sortedPlans={sortedPlans}
          hasSeatPlan={hasSeatPlan}
          seatsAllowed={seatsAllowed}
          hasActiveSub={hasActiveSub}
          handleSubscribe={handleSubscribe}
          checkoutLoading={checkoutLoading}
        />
      )}
    </DashboardLayout>
  );
}

// We wrap the Dashboard with the ToastProvider at this level
// so that all internal Dashboard hooks can access toasts.
export default function Dashboard(props) {
  return (
    <ToastProvider>
      <DashboardContent {...props} />
    </ToastProvider>
  );
}
